<script setup lang="ts">
import {PropType, ref} from "vue";
import {CrewMember} from "@/models/CrewMember";
import {useI18n} from "vue-i18n";
import {useFormatStore} from "@/stores/formatStore";
import MemberDeleteModalTrip from "@/components/CrewCollapsible/components/MemberDeleteModalTrip.vue";
import MemberEditModal from "@/components/CrewCollapsible/components/MemberEditModal.vue";
import {useRoute} from "vue-router";
import MemberDeleteModalList from "@/components/CrewCollapsible/components/MemberDeleteModalList.vue";
import {useCrewStore} from '@/stores/crewStore';
import {useTripsStore} from "@/stores/tripsStore";
import UserInfo from "@/components/UserInfo/UserInfo.vue";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";


const { t } = useI18n();
const formatStore = useFormatStore();
const crewStore = useCrewStore();
const tripsStore = useTripsStore();

const { member, loading } = defineProps({
  member: {
    type: Object as PropType<CrewMember>,
    required: true
  },
  loading: {
    type: Boolean,
    default: false,
    required: false
  }
})
const route = useRoute();
const open = ref(false);
const showDeleteFromTripModal = ref(false);
const showDeleteFromListModal = ref(false);
const showEditModal = ref(false);

async function onDeleteFromList(member:CrewMember) {
  try {
    const listId = crewStore.crewList.uuid;
    await crewStore.removeCrewMember(listId, member.uuid);
    showDeleteFromListModal.value = false;
    await crewStore.fetchCrewList(route.params.id as string);
  } catch (error) {
    console.error('Error deleting member:', error);
  }
}


async function onDeleteFromTrip() {
  try {
    const tripSlug = tripsStore.trip.slug;
    console.log(`Attempting to delete member: ${member.uuid} from trip ${tripSlug}` );
    await tripsStore.removeCrewMember(tripSlug, member.uuid);
    showDeleteFromTripModal.value = false;
    await tripsStore.loadTrip(tripSlug);

    console.log('Crew member deleted successfully');
  } catch (error) {
    console.error('Failed to delete crew member:', error);
  }
}

function truncateFileName(name: string) {
  if (!name) return '';
  if (name.length > 10) return name.slice(0, 8) + '..';
  return name;
}

</script>

<template>
  <div v-if="loading" class="flex justify-center items-center py-8">
    <CustomSpinner/>
  </div>
  <div v-else class="bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue rounded-2xl desktop mb-3 min-h-20">
    <div class=" flex flex-row items-center invoice-row py-4 pr-10 justify-between">
      <div class="responsive-body-large font-bold flex flex-row items-center gap-3 w-2/5">
        <div class="flex flex-row gap-4">
        <UserInfo
            :member="member"
            width="41"
            font_size="responsive-body-large"
        >
          <div class="flex flex-row gap-1 lg:hidden">
            <div v-if="member.isLeader" class="responsive-body-xsmall rounded-15xl h-fit w-fit px-2 pt-0.5 bg-black text-white">
              <span>{{ t('tripOverview.leader') }}</span>
            </div>
          </div>
        </UserInfo>
          <div class="hidden lg:flex lg:flex-row lg:justify-center lg:gap-4 lg:items-center">
            <div v-if="member.isLeader" class="responsive-body-xsmall rounded-15xl h-fit w-fit justify-center px-2 pt-0.5 bg-black text-white">
              <span>{{ t('tripOverview.leader') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col lg:flex-row gap-5 justify-start items-start w-3/5 responsive-body-small text-left pl-5 pr-5">
        <!-- Phone -->
        <div class="flex items-center gap-1.5">
          <img src="@/assets/icons/svg/Phone_16x16.svg" v-if="member.phone" class="w-4 h-4 object-contain">
          <a v-if="member.phone" :href="`tel:${member.phone}`"
             class="hover:underline whitespace-nowrap"
          >
            {{ member.phone }}
          </a>
        </div>

        <!-- Email -->
        <div class="flex items-center justify-center gap-1">
          <img src="@/assets/icons/svg/Mail_16x16.svg">
          <a v-if="member.email" :href="`mailto:${member.email}`" class="hover:underline break-words">
            {{ member.email }}
          </a>
        </div>
      </div>

      <div class="flex flex-row items-center w-1/5 responsive-body-xsmall">
        <div v-if="(!member.nationality || !member.birthDate || !member.passportNumber || !member.passportExpiryDate) && !member.invitedByEmail"
             class="bg-notifications-actionRequired flex flex-row justify-center items-center w-fit px-2 h-6 gap-2 rounded-11xl text-xs">
          <img src="@/assets/icons/svg/Action_required_16x16.svg">
          <span>{{ t('crew.incomplete') }}</span>
        </div>
        <div v-else-if="member.invitedByEmail"
             class="bg-notifications-pending flex flex-row justify-center items-center px-4 py-1 gap-2 rounded-11xl whitespace-nowrap">
          <img src="@/assets/icons/svg/Mail_16x16.svg">
          {{ t('crew.invitedByEmail') }}
        </div>
        <div v-else class="w-[12vw]"></div>
      </div>
      <div @click="open = !open" class="cursor-pointer ml-13">
        <img src="@/assets/icons/svg/Up_16x16.svg" v-if="open" class="w-4 h-4 object-contain" />
        <img src="@/assets/icons/svg/Down_16x16.svg" v-else class="w-4 h-4 object-contain" />
      </div>

      <div class="relative" >


      <ElDropdown
            trigger="click"
            placement="bottom-end"
            :popper-options="{
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 10
          }
        },
        {
          name: 'offset',
          options: {
            offset: [0, 4]
          }
        }
      ]
    }"
        >
          <img src="@/assets/icons/svg/Menu dots_16x16.svg" class="ml-8 mt-1">
          <template #dropdown>
            <ElDropdownMenu>
              <ElDropdownItem class="text-black flex gap-2" @click="showEditModal = true">
                <img src="@/assets/icons/svg/Edit_24x24.svg" />
                {{ t('crew.editMember') }}
              </ElDropdownItem>
              <ElDropdownItem v-if="route.name === 'trip-crew'" class="text-black flex gap-2" @click="showDeleteFromTripModal = true">
                <img src="@/assets/icons/svg/Delete_24x24.svg" />
                  {{ t('crew.deleteMemberTrip') }}
              </ElDropdownItem>
              <ElDropdownItem v-else class="text-black flex gap-2" @click="showDeleteFromListModal = true">
                <img src="@/assets/icons/svg/Delete_24x24.svg" />
                  {{ t('crew.deleteMemberList') }}
              </ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </div>

    </div>
    <div v-if="open" class="py-6 expand px-24">
      <!-- Grid for equal spacing of 4 columns -->
      <div class="grid grid-cols-4 w-full">
        <!-- Nationality -->
        <div>
          <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.nationality') }}</div>
          <div v-if="member.nationality" class="responsive-body-medium">{{ member.nationality.name }}</div>
          <div v-else class="bg-notifications-actionRequired flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
            <img src="@/assets/icons/svg/Action_required_16x16.svg">
            {{ t('crew.required') }}
          </div>
        </div>
        <!-- Date of birth -->
        <div>
          <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.birthDate') }}</div>
          <div v-if="member.birthDate" class="responsive-body-medium">{{ formatStore.formatDate(new Date(member.birthDate ?? new Date())) }}</div>
          <div v-else class="bg-notifications-actionRequired flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
            <img src="@/assets/icons/svg/Action_required_16x16.svg">
            {{ t('crew.required') }}
          </div>
        </div>
        <!-- Passport -->
        <div>
          <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.passport') }}</div>
          <div v-if="member.passportNumber" class="responsive-body-medium">{{ member.passportNumber }}</div>
          <div v-else class="bg-notifications-actionRequired flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
            <img src="@/assets/icons/svg/Action_required_16x16.svg">
            {{ t('crew.required') }}
          </div>
        </div>
        <!-- Passport expiry -->
        <div>
          <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.passportExpiryDate') }}</div>
          <div v-if="member.passportExpiryDate" class="responsive-body-medium">{{ formatStore.formatDate(new Date(member.passportExpiryDate)) }}</div>
          <div v-else class="bg-notifications-actionRequired flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
            <img src="@/assets/icons/svg/Action_required_16x16.svg">
            {{ t('crew.required') }}
          </div>
        </div>
      </div>
      <!-- Row below the grid: Passport, Sailing & VHF licenses -->
      <div class="mt-6 grid grid-cols-4 responsive-body-medium">
        <div>
          <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.passportDocuments') }}</div>
          <div class="flex flex-row items-start gap-1">
            <div v-if="member.passportDocuments && member.passportDocuments.length">
              <div v-for="doc in member.passportDocuments" :key="doc.uuid" class="flex items-center gap-1">
                {{ (doc.fileName) }}
                <a
                  :href="doc.fileUrl"
                  :download="doc.fileName"
                  target="_blank"
                  rel="noopener"
                >
                  <img src="@/assets/icons/svg/Download_16x16.svg" class="download-icon" />
                </a>
              </div>
            </div>
            <div v-else>{{ t('crew.no') }}</div>
          </div>
        </div>
        <div>
          <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.sailingLicense') }}</div>
          <div class="flex flex-row items-start gap-1">
            <div v-if="member.licenseDocuments && member.licenseDocuments.length">
              <div v-for="doc in member.licenseDocuments" :key="doc.uuid" class="flex items-center gap-1">
                {{ (doc.fileName) }}
                <a
                  :href="doc.fileUrl"
                  :download="doc.fileName"
                  target="_blank"
                  rel="noopener"
                >
                  <img src="@/assets/icons/svg/Download_16x16.svg" class="download-icon" />
                </a>
              </div>
            </div>
            <div v-else>{{ t('crew.no') }}</div>
          </div>
        </div>
        <div>
          <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.vhfLicense') }}</div>
          <div class="flex flex-row items-start gap-1">
            <div v-if="member.vhfLicenseDocuments && member.vhfLicenseDocuments.length">
              <div v-for="doc in member.vhfLicenseDocuments" :key="doc.uuid" class="flex items-center gap-1">
                {{ (doc.fileName) }}
                <a
                  :href="doc.fileUrl"
                  :download="doc.fileName"
                  target="_blank"
                  rel="noopener"
                >
                  <img src="@/assets/icons/svg/Download_16x16.svg" class="download-icon" />
                </a>
              </div>
            </div>
            <div v-else>{{ t('crew.no') }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile -->
  <div class="bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue rounded-2xl min-w-fit phone mb-4">
    <div class="flex flex-row items-center invoice-row py-4 px-5 justify-between">
      <div class=" responsive-body-large font-bold flex flex-col lg:flex-row items-center gap-3">
        <div class="flex flex-row gap-2">

          <UserInfo
              :member="member"
              width="41"
              font_size="responsive-body-large"
          >
            <div class="flex flex-row gap-1 lg:hidden">
              <div v-if="member.isLeader" class="responsive-body-xsmall rounded-15xl h-fit w-fit px-2 pt-0.5 bg-black text-white">
                <span>{{ t('tripOverview.leader') }}</span>
              </div>
            </div>
          </UserInfo>
          <div class="hidden sm:flex sm:flex-row sm:justify-center sm:gap-4 sm:items-center">
            <div v-if="member.isLeader" class="responsive-body-xsmall rounded-15xl h-fit w-fit justify-center px-2 pt-0.5 bg-black text-white">
              <span>{{ t('tripOverview.leader') }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="flex">
        <div @click="open = !open" class="cursor-pointer ml-8 mt-1">
          <img src="@/assets/icons/svg/Up_16x16.svg" v-if="open" class="object-contain ">
          <img src="@/assets/icons/svg/Down_16x16.svg" v-else class="object-contain">
        </div>

        <div class="relative">
          <ElDropdown
              trigger="click"
              placement="bottom-end"
              :popper-options="{
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                      padding: 8
                    }
                  },
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 4]
                    }
                  }
                ]
              }"
            >
            <img src="@/assets/icons/svg/Menu dots_16x16.svg" class=" ml-6 sm:ml-8">
            <template #dropdown>
              <ElDropdownMenu>
                <ElDropdownItem class="text-black flex gap-2" @click="showEditModal = true">
                  <img src="@/assets/icons/svg/Edit_24x24.svg" />
                  {{ t('crew.editMember') }}
                </ElDropdownItem>
                <ElDropdownItem v-if="route.name === 'trip-crew'" class="text-black flex gap-2" @click="showDeleteFromTripModal = true">
                  <img src="@/assets/icons/svg/Delete_24x24.svg" />
                  {{ t('crew.deleteMemberTrip') }}
                </ElDropdownItem>
                <ElDropdownItem v-else class="text-black flex gap-2" @click="showDeleteFromListModal = true">
                  <img src="@/assets/icons/svg/Delete_24x24.svg" />
                  {{ t('crew.deleteMemberList') }}
                </ElDropdownItem>
              </ElDropdownMenu>
            </template>
          </ElDropdown>
        </div>
      </div>
    </div>
    <div>
      <div class="flex flex-col gap-2 px-5 pb-3 pt-1 responsive-body-small">
        <div class="flex flex-row items-center gap-2">
          <img src="@/assets/icons/svg/Phone_16x16.svg" v-if="member.phone">
          <a v-if="member.phone" :href="`tel:${member.phone}`" class="hover:underline">
            {{ member.phone }}
          </a>
        </div>
        <div class="flex flex-row items-center gap-2">
          <img src="@/assets/icons/svg/Mail_16x16.svg">
          <a v-if="member.email" :href="`mailto:${member.email}`" class="hover:underline">
            {{ member.email }}
          </a>
        </div>
      </div>
      <div class="flex flex-row items-center px-4 pb-4 responsive-body-xsmall">
        <div v-if="(!member.nationality || !member.birthDate || !member.passportNumber || !member.passportExpiryDate) && !member.invitedByEmail"
          class="bg-notifications-actionRequired  flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
          <img src="@/assets/icons/svg/Action_required_16x16.svg">
          {{ t('crew.incomplete') }}
        </div>
        <div v-if="member.invitedByEmail"
          class="bg-notifications-pending flex flex-row items-center px-4 py-1 gap-2 rounded-11xl">
          <img src="@/assets/icons/svg/Mail_16x16.svg">
          {{ t('crew.invitedByEmail') }}
        </div>
      </div>
    </div>
    <div v-if="open" class="px-5 py-2.5">
      <div class="card-container">
        <div class="card">
          <div class="flex gap-10">
            <div class="card-section" v-if="member.nationality">
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.nationality') }}:</div>
              <div class="responsive-body-medium">{{ member.nationality.name }}</div>
            </div>
            <div class="card-section" v-else>
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.nationality') }}:</div>
              <div class="bg-notifications-actionRequired flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
                <img src="@/assets/icons/svg/Action_required_16x16.svg">
                {{ t('crew.required') }}
              </div>
            </div>

            <div class="card-section" v-if="member.birthDate">
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.birthDate') }}:</div>
              <div class="responsive-body-medium">{{ formatStore.formatDate(new Date(member.birthDate ?? new Date())) }}</div> <!-- Updated -->
            </div>
            <div class="card-section" v-else>
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.birthDate') }}:</div>
              <div class="bg-notifications-actionRequired flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
                <img src="@/assets/icons/svg/Action_required_16x16.svg">
                {{ t('crew.required') }}
              </div>
            </div>

          </div>
          <div class="flex gap-10">
            <div class="card-section" v-if="member.passportNumber">
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.passport') }}:</div>
              <div class="responsive-body-medium">{{ member.passportNumber }}</div>
            </div>
            <div class="card-section" v-else>
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.passport') }}:</div>
              <div class="bg-notifications-actionRequired flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
                <img src="@/assets/icons/svg/Action_required_16x16.svg">
                {{ t('crew.required') }}
              </div>
            </div>
            <div class="card-section" v-if="member.passportExpiryDate">
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.passportExpiryDate') }}:</div>
              <div class="responsive-body-medium">{{ formatStore.formatDate(new Date(member.passportExpiryDate)) }}</div> <!-- Updated -->
            </div>
            <div class="card-section" v-else>
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.passportExpiryDate') }}:</div>
              <div class="bg-notifications-actionRequired flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
                <img src="@/assets/icons/svg/Action_required_16x16.svg">
                {{ t('crew.required') }}
              </div>
            </div>
          </div>
          <div class="flex gap-10">
            <div class="card-section">
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.passportDocuments') }}:</div>
              <div class="flex flex-row items-start gap-1">
                <div v-if="member.passportDocuments && member.passportDocuments.length">
                  <div v-for="doc in member.passportDocuments" :key="doc.uuid" class="flex items-center gap-1">
                   {{ truncateFileName(doc.fileName) }}
                    <a
                        :href="doc.fileUrl"
                        :download="doc.fileName"
                        target="_blank"
                        rel="noopener"
                    >
                      <img src="@/assets/icons/svg/Download_16x16.svg" class="download-icon" />
                    </a>
                  </div>
                </div>
                <div v-else>{{ t('crew.no') }}</div>
              </div>
            </div>
          </div>
          <div class="flex gap-10">
            <div class="card-section">
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.sailingLicense') }}:</div>
              <div class="flex flex-row items-start gap-1">
                <div v-if="member.licenseDocuments && member.licenseDocuments.length">
                  <div v-for="doc in member.licenseDocuments" :key="doc.uuid" class="flex items-center gap-1">
                   {{ truncateFileName(doc.fileName) }}
                    <a
                        :href="doc.fileUrl"
                        :download="doc.fileName"
                        target="_blank"
                        rel="noopener"
                    >
                      <img src="@/assets/icons/svg/Download_16x16.svg" class="download-icon" />
                    </a>
                  </div>
                </div>
                <div v-else>{{ t('crew.no') }}</div>
              </div>
            </div>
            <div class="card-section">
              <div class="text-black/40 font-extralight responsive-body-xsmall">{{ t('crew.vhfLicense') }}:</div>
              <div class="flex flex-row items-start gap-1">
                <div v-if="member.vhfLicenseDocuments && member.vhfLicenseDocuments.length">
                  <div v-for="doc in member.vhfLicenseDocuments" :key="doc.uuid" class="flex items-center gap-1">
                    {{ truncateFileName(doc.fileName) }}
                    <a
                        :href="doc.fileUrl"
                        :download="doc.fileName"
                        target="_blank"
                        rel="noopener"
                    >
                      <img src="@/assets/icons/svg/Download_16x16.svg" class="download-icon" />
                    </a>
                  </div>
                </div>
                <div v-else>{{ t('crew.no') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <MemberDeleteModalTrip
      :member="member"
      :showDeleteModal="showDeleteFromTripModal"
      @on-close="showDeleteFromTripModal = false"
      @on-delete-click="onDeleteFromTrip"
  />
  <MemberDeleteModalList
      :member="member"
      :showDeleteModal="showDeleteFromListModal"
      @on-close="showDeleteFromListModal = false"
      @on-delete-click="onDeleteFromList"
  />
  <MemberEditModal
      :member="member"
      :showEditModal="showEditModal"
      @on-close="showEditModal = false"
  />


</template>

<style scoped>
@media (min-width: 769px) and (max-width: 1335px) {
  .expand{
    padding-right: 45px ;
    padding-left: 45px;
  }
}

.card {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
}

.card-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.card-section {
  margin-bottom: 8px;
  font-size: 14px;
  min-width: 50%;
}

.status-required {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffcca7;
  height: 25px;
  border-radius: 15px;
  font-size: 12px;
  gap: 8px;
}

.download-icon {
  cursor: pointer;
  margin-left: 4px;
}

strong {
  font-weight: bold;
}

@media (min-width: 769px) {
  .phone {
    display: none;
  }
}

@media (max-width: 768px) {
  .desktop {
    display: none;
  }
}
</style>
